import React, { useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Mail, HelpCircle, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { contactAPI } from '../services/api';
import { Link } from 'react-router-dom';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'challenge',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Try backend API first (for local development)
      if (process.env.REACT_APP_BACKEND_URL) {
        await contactAPI.submit(formData.name, formData.email, formData.subject, formData.message);
      } else {
        // Use PHP handler (for production on GoDaddy)
        const response = await fetch(`${window.location.origin}/contact-handler.php`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData)
        });
        
        const result = await response.json();
        
        if (!response.ok || !result.success) {
          throw new Error(result.message || 'Failed to send message');
        }
      }
      
      toast({
        title: 'Message Sent!',
        description: 'We\'ll get back to you within 24 hours.',
      });
      setFormData({ name: '', email: '', subject: 'challenge', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to send message. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-black to-[#1A1A1A] text-white border-b border-[#10dfb2]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            Get In <span className="text-[#10dfb2]">Touch</span>
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto">
            Our team is here to help you with any questions
          </p>
        </div>
      </section>

      {/* Contact Options & Form */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1A1A1A] rounded-full mb-4">
                  <Mail className="text-[#10dfb2]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Email Us
                </h3>
                <p className="text-gray-600 mb-4">
                  Get a response within 24 hours
                </p>
                <a
                  href="mailto:support@thefundedlab.com"
                  className="text-[#0066CC] hover:underline font-semibold"
                >
                  support@thefundedlab.com
                </a>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1A1A1A] rounded-full mb-4">
                  <HelpCircle className="text-[#10dfb2]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Support Center
                </h3>
                <p className="text-gray-600 mb-4">
                  Browse FAQs and common questions
                </p>
                <Link to="/faq">
                  <Button className="bg-[#10dfb2] hover:bg-[#10dfb2] text-black font-semibold">
                    View FAQs
                  </Button>
                </Link>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#1A1A1A] rounded-full mb-4">
                  <Send className="text-[#10dfb2]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Social Media
                </h3>
                <p className="text-gray-600 mb-4">
                  Connect with us on social
                </p>
                <div className="flex justify-center space-x-4">
                  <a href="https://discord.com/invite/BBXZKcDK" target="_blank" rel="noopener noreferrer" className="text-[#10dfb2] hover:text-[#10dfb2]">
                    Discord
                  </a>
                  <a href="#" className="text-[#10dfb2] hover:text-[#10dfb2]">
                    Telegram
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <div className="max-w-3xl mx-auto">
            <Card className="border-none shadow-xl">
              <CardContent className="p-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  Send Us a Message
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="name" className="text-gray-700 font-semibold">
                      Name
                    </Label>
                    <Input
                      id="name"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-semibold">
                      Email
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-gray-700 font-semibold">
                      I'm interested in
                    </Label>
                    <Select
                      value={formData.subject}
                      onValueChange={(value) => setFormData({ ...formData, subject: value })}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select a topic" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="challenge">Challenge Program</SelectItem>
                        <SelectItem value="instant">Instant Funding</SelectItem>
                        <SelectItem value="support">Technical Support</SelectItem>
                        <SelectItem value="partnership">Partnership</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-gray-700 font-semibold">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      placeholder="Tell us how we can help you..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={6}
                      className="mt-2"
                    />
                  </div>
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-[#10dfb2] hover:bg-[#10dfb2] text-black font-semibold text-lg py-6"
                    disabled={submitting}
                  >
                    {submitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
